/**
 * CSV for imports and exports (RFC 4180), the same on the API and in the browser.
 *
 * - Exports start with a byte-order mark so Excel reads Indian names in UTF-8, then "# " lines (who downloaded it,
 *   when, that it's confidential), then the header row.
 * - A cell a spreadsheet would run as a formula (=, +, -, @, tab, carriage return) is written with a leading ' so it
 *   shows as text: someone's "name" can't become a formula on the office computer that opens the file.
 * - Imports skip blank lines and "# " lines, so an exported file can be edited and brought back.
 */

export type CsvValue = string | number | boolean | null | undefined;

const FORMULA_START = /^[=+\-@\t\r]/;
const NEEDS_QUOTES = /[",\r\n]/;

export function csvCell(value: CsvValue): string {
  if (value === null || value === undefined) return "";
  let text = String(value);
  if (FORMULA_START.test(text)) text = `'${text}`;
  return NEEDS_QUOTES.test(text) || text !== text.trim() ? `"${text.replaceAll('"', '""')}"` : text;
}

export function toCsv(
  rows: readonly (readonly CsvValue[])[],
  options: { notes?: readonly string[] } = {},
): string {
  const notes = (options.notes ?? []).map((note) => `# ${note.replace(/[\r\n]+/g, " ")}`);
  const lines = [...notes, ...rows.map((row) => row.map(csvCell).join(","))];
  return `﻿${lines.join("\r\n")}\r\n`;
}

export class CsvParseError extends Error {
  constructor(
    message: string,
    readonly line: number,
  ) {
    super(message);
    this.name = "CsvParseError";
  }
}

/** One record and the line it started on (1-based), for error messages people can find. */
export interface CsvRecord {
  line: number;
  cells: string[];
}

/** Reads CSV text into records. Throws CsvParseError for an unclosed quote. */
export function parseCsv(text: string): CsvRecord[] {
  const source = text.startsWith("﻿") ? text.slice(1) : text;
  const records: CsvRecord[] = [];
  let cells: string[] = [];
  let cell = "";
  let quoted = false;
  let line = 1;
  let recordLine = 1;
  let atCellStart = true;

  const endRecord = () => {
    cells.push(cell);
    const blank = cells.every((value) => value.trim() === "");
    const comment = cells[0]?.startsWith("#") ?? false;
    if (!blank && !comment) records.push({ line: recordLine, cells });
    cells = [];
    cell = "";
    atCellStart = true;
  };

  for (let index = 0; index < source.length; index += 1) {
    const char = source[index];
    if (quoted) {
      if (char === '"') {
        if (source[index + 1] === '"') {
          cell += '"';
          index += 1;
        } else {
          quoted = false;
        }
      } else {
        if (char === "\n") line += 1;
        cell += char;
      }
      continue;
    }
    if (char === '"' && atCellStart) {
      quoted = true;
      atCellStart = false;
    } else if (char === ",") {
      cells.push(cell);
      cell = "";
      atCellStart = true;
    } else if (char === "\n" || char === "\r") {
      if (char === "\r" && source[index + 1] === "\n") index += 1;
      endRecord();
      line += 1;
      recordLine = line;
    } else {
      cell += char;
      atCellStart = false;
    }
  }
  if (quoted) throw new CsvParseError('A quote (") is opened but never closed', recordLine);
  if (cell !== "" || cells.length > 0) endRecord();
  return records;
}

/** A value written by csvCell, read back: the ' that kept a formula as text comes off again. */
export function fromCsvCell(value: string): string {
  const trimmed = value.trim();
  return trimmed.startsWith("'") && FORMULA_START.test(trimmed.slice(1))
    ? trimmed.slice(1)
    : trimmed;
}
